import { useSeo } from '@/lib/seo';
import Header from '@/components/Header';
import { Link } from 'react-router-dom';
import { Sparkles, CloudRain, Truck, Waypoints, MapPin, Activity, Route, Shield, Bell, Gift, Camera, Monitor, MonitorPlay } from 'lucide-react';

// PUBLIC release notes only — user-facing improvements. No admin/internal/refactor items.
const RELEASES = [
  {
    date: '2026-08-05',
    tag: 'New',
    title: 'VMS message-sign wall, live travel times & earthquake alerts',
    icon: <MonitorPlay className="w-5 h-5" />,
    items: [
      'New VMS wall (Business): a dedicated full-screen wall of NZTA electronic message signs (More → VMS wall). Pick the signs you want, choose a 4–12 tile layout, and each board updates itself every 30 seconds — perfect for a NOC TV alongside your CCTV wall.',
      'Sign change alerts: tap the bell on any sign in the VMS picker to get a push/in-portal alert the moment that board’s message changes — so you never miss a new “strong winds” or “road closed” warning.',
      'Assign a VMS wall to a TV exactly like CCTV — approve the on-screen 6-digit code from your phone, or open a private screen link. One screen can show NOC, CCTV or VMS.',
      'Live travel times on your dashboard (Business): a board showing current journey times and delays on your subscribed highways and saved corridors, refreshing every minute.',
      'Earthquake alerts near your routes: turn on GeoNet quake alerts in Settings and get warned when a felt quake (M4.0+) lands within a distance you choose of one of your saved routes — a heads-up for possible slips, bridge checks and closures.',
      'The NOC overview now shows a live VMS layer on the map, a delays strip, a recent-quakes panel, and a per-feed status bar (NZTA, MetService, VMS, Journeys, GeoNet).',
    ],
  },
  {
    date: '2026-08-04',
    tag: 'New',
    title: 'Set up a NOC wall TV in seconds + auto-rotate',
    icon: <Monitor className="w-5 h-5" />,
    items: [
      'Wall TVs no longer get stuck at the login screen. Open the CCTV wall on the TV and it shows a 6-digit code — approve it from your phone in Admin → NOC Screens, and the TV signs itself in. No keyboard needed.',
      'Prefer a link? Create a named, private sign-in link for each screen and open it once on the TV. Links can be revoked any time, and we warn you before one is about to expire so walls never drop offline.',
      'New optional Auto-rotate mode: pick more cameras than the layout shows and the wall sweeps through them a page at a time — so a single screen can cover a whole region.',
      'The NOC overview and CCTV wall remain available to Admins and Business users.',
    ],
  },
  {
    date: '2026-08-03',
    tag: 'New',
    title: 'CCTV camera wall for your NOC (Business)',
    icon: <Camera className="w-5 h-5" />,
    items: [
      'Business plan unlocks a live CCTV wall (More → CCTV wall) showing NZTA traffic cameras from right across NZ — over 300 of them, refreshing every few seconds.',
      'Build your own view: tick the exact cameras you want by island, region or highway, and choose a wall layout from 4 up to 12 tiles for big control-room TVs.',
      'Any camera whose highway has a live closure or crash right now lights up with a red or orange border and a banner — and you can turn on a soft chime that sounds the moment a tile flips to a closure.',
      'Save named presets (e.g. “Auckland AM”, “SH1 South”) and switch between them in one tap. Turn on Motion mode for a time-lapse feel from the latest stills.',
      'On the NOC overview map, tap the Cameras toggle then click any camera pin to drop it straight onto your CCTV wall.',
      'The NOC overview and CCTV wall are available to Admins and Business users.',
    ],
  },
  {
    date: '2026-07-27',
    tag: 'New',
    title: 'Road closures now nationwide — six more councils added',
    icon: <MapPin className="w-5 h-5" />,
    items: [
      'On top of Auckland & Wellington, we now pull local road closures and restrictions from Canterbury (covering Christchurch, Ashburton, Timaru and the wider region), Far North, Gisborne, Marlborough, Hastings and Clutha — refreshed every 30 minutes.',
      'Every closure is geo-fenced to your saved routes and planned loads, so a council closure on your corridor is flagged automatically in Trip Watch and the Load hazard scan.',
      'Filter the Alerts list by “All local councils”, and see each council’s live status on the Feed Status page.',
      'More regions will keep rolling out — this is built to plug in any council that publishes an open map feed.',
    ],
  },
  {
    date: '2026-07-25',
    tag: 'New',
    title: 'Wellington City Council closures now live',
    icon: <MapPin className="w-5 h-5" />,
    items: [
      'We now poll Wellington City Council every 30 minutes for approved street events and road closures — current and upcoming.',
      'Just like Auckland, any Wellington closure that falls on your saved route or planned-load corridor is automatically flagged in Trip Watch and your Load hazard scan.',
      'Find them on the Live Map and filter the Alerts list by “Wellington (WCC)”.',
    ],
  },
  {
    date: '2026-07-24',
    tag: 'New',
    title: 'Auckland Transport closures & freight restrictions',
    icon: <MapPin className="w-5 h-5" />,
    items: [
      'We now poll Auckland Transport every 30 minutes for active road closures and worksites — they show on the Live Map and Alerts list (filter “Auckland (AT)”).',
      'Planning a trip through Auckland? Any AT worksite that falls on your saved route or planned-load corridor is automatically flagged in Trip Watch and your Load hazard scan — no more surprises mid-route.',
      'New Auckland freight layers on the Freight Restrictions map: height limits, tunnel restrictions, 50MAX, HPMV detours, and over-dimension / over-weight routes.',
      'This is the first of our local-council feeds — more regions to follow.',
    ],
  },
  {
    date: '2026-07-22',
    tag: 'New',
    title: 'Try Plus (and SMS) free for 7 days',
    icon: <Gift className="w-5 h-5" />,
    items: [
      'New here? Start a 7-day free trial of Plus from Settings → Plan & Billing — no card required.',
      'Your trial unlocks Route/Corridor Watch, unlimited highway subscriptions, email and web-push alerts.',
      'Want to try text alerts too? Add your mobile to also trial paid SMS alerts free — you’ll get critical road and weather warnings by text during the trial.',
      'A countdown on your dashboard shows how many days are left; when it ends you can choose a plan to keep going.',
    ],
  },
  {
    date: '2026-07-16',
    tag: 'New',
    title: 'Smart route-hazard scanner for Load Pilots',
    icon: <Shield className="w-5 h-5" />,
    items: [
      'Smart event intelligence — we don’t just relay raw NZTA alerts. Every single event is read and understood for the details that actually matter to freight and oversize loads, so a critical restriction buried in the fine print of a roadworks notice becomes a clear, actionable heads-up instead of getting missed.',
      'Every planned load now auto-scans live NZTA events along your actual route and flags the ones that matter for wide/heavy loads.',
      'Detects width & lane restrictions (single lane, stop/go, narrowing, turning restrictions, diversions), slips / washouts / dropouts, weight & structural limits, and over-dimension / height restrictions.',
      'Full closures and single-lane sections are marked critical (red); narrowing, diversions and other restrictions are marked review (amber) for you to judge — NZTA wording rarely states exact widths.',
      'See hazards live while you build a corridor in the editor, and on every load card next to the clearance check.',
      'Get a push + email alert the moment a NEW hazard (e.g. an overnight slip or fresh lane closure) appears on a route you’ve already planned.',
      'When NZTA states an actual width, height or weight and you’ve entered your load’s dimensions, we auto-escalate a “won’t fit” / “over limit” to a red blocker — otherwise we surface it for you to judge.',
      'These events also now show under the “Freight-relevant” layer on the Live NZ Map.',
    ],
  },
  {
    date: '2026-07-16',
    tag: 'Improved',
    title: 'Route watch now geo-fenced to your actual drive',
    icon: <Route className="w-5 h-5" />,
    items: [
      'Saved routes now match alerts to your real drive path — no more false alerts for an event on the same highway number but far off your route (e.g. Kaikoura no longer shows on a Christchurch → Dunedin trip).',
      'Choose an alert radius per route: Tight (3 km), Standard (8 km) or Wide (15 km) for freight detours.',
      'The “Can I drive?” banner now opens a route-filtered alerts view for just that corridor.',
    ],
  },
  {
    date: '2026-07-15',
    tag: 'New',
    title: 'Cutting-edge planning: clearance checks, live map, trip status & team tools',
    icon: <Waypoints className="w-5 h-5" />,
    items: [
      'Auto clearance check on every load — do-not-cross bridges, 50MAX/HPMV and height/weight limits on your corridor are flagged as blockers, tight or OK.',
      '“Can I drive?” status banner on your dashboard — an instant clear / delays / disruption read-out across your saved routes.',
      'New full-screen Live NZ Map with road, weather and freight layers you can toggle.',
      'Shareable live tracking links — send escorts or clients a public link showing your load’s route, waypoints and progress.',
      'Team management: create an organisation, invite staff by email, manage seats, and see a shared team load schedule.',
    ],
  },
  {
    date: '2026-07-15',
    tag: 'New',
    title: 'Drag-and-drop load waypoints + smart freight alerts',
    icon: <Waypoints className="w-5 h-5" />,
    items: [
      'Load Pilots can now drop waypoint pins straight onto the map — click to add, drag to adjust, and each pin is auto-named.',
      'Freight restrictions (do-not-cross bridges, 50MAX, HPMV) show on the planning map so you can route around pinch-points.',
      'New hourly “freight restriction changed” alerts: get notified when a bridge condition or axle limit changes on a highway you watch.',
      'Live road events involving lane narrowing or width restrictions are now tagged with a Freight badge.',
    ],
  },
  {
    date: '2026-07-15',
    tag: 'Guide',
    title: 'New Help & Guide centre',
    icon: <Bell className="w-5 h-5" />,
    items: [
      'A step-by-step guide covering sign-up, customising your alerts, notification channels, weather warnings and Load Pilot planning.',
      'Find it any time from the “Guide” link in the top menu.',
    ],
  },
  {
    date: '2026-07-14',
    tag: 'New',
    title: 'MetService weather warnings',
    icon: <CloudRain className="w-5 h-5" />,
    items: [
      'Official MetService warnings (rain, snow, wind, thunderstorms) now appear alongside road events, colour-matched to severity.',
      'Weather zones are drawn on the map and auto-clear when MetService lifts them.',
      'Prefer road-only? You can switch weather warnings off in Settings.',
    ],
  },
  {
    date: '2026-07-14',
    tag: 'New',
    title: 'Public feed status & oversize load watch',
    icon: <Activity className="w-5 h-5" />,
    items: [
      'A public status page shows whether the NZTA, MetService and freight feeds are live.',
      'Drivers can see upcoming oversize loads on their route (pilot contact details stay private).',
    ],
  },
  {
    date: '2026-07-13',
    tag: 'New',
    title: 'Trip Watch — road + weather for your corridor',
    icon: <Route className="w-5 h-5" />,
    items: [
      'Your dashboard now combines road events and weather warnings for each saved trip, with a most-restrictive headline.',
    ],
  },
  {
    date: '2026-07-09',
    tag: 'Improved',
    title: 'Better live map & clearer events',
    icon: <MapPin className="w-5 h-5" />,
    items: [
      'Closures and multi-segment events (like flood road closures) now draw their full route on the map.',
      'Events show plain-English causes (Flooding, Snow & Ice, Slip, Strong Winds) instead of generic labels.',
    ],
  },
  {
    date: '2026-07-07',
    tag: 'New',
    title: 'Route/Corridor Watch & freight restrictions',
    icon: <Shield className="w-5 h-5" />,
    items: [
      'Save a start → destination corridor and get warned about anything on the way.',
      'Freight restrictions map for transport operators: crane-bridge do-not-cross points, 50MAX and HPMV routes, axle limits.',
    ],
  },
];

const TAG_STYLE = {
  New: 'bg-teal-100 text-teal-700 border-teal-200',
  Improved: 'bg-orange-100 text-orange-700 border-orange-200',
  Guide: 'bg-sky-100 text-sky-700 border-sky-200',
};

export default function WhatsNew() {
  useSeo({ title: "What's New", description: 'The latest features and improvements to PTT NZ Alerts.' });
  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-10">
        <div className="mb-8">
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-1 flex items-center gap-2"><Sparkles className="w-4 h-4 text-orange-500" /> Product updates</div>
          <h1 className="font-display font-black text-3xl md:text-4xl">What's New</h1>
          <p className="text-zinc-600 mt-2">The latest improvements to PTT NZ Alerts. New to the platform? Start with the <Link to="/help" className="text-orange-700 underline">Guide</Link>.</p>
        </div>

        <div className="relative border-l-2 border-zinc-200 pl-6 space-y-8" data-testid="whatsnew-timeline">
          {RELEASES.map((r, i) => (
            <div key={i} className="relative" data-testid={`release-${i}`}>
              <span className="absolute -left-[31px] top-1.5 w-4 h-4 rounded-full bg-orange-500 ring-4 ring-orange-100" />
              <div className="card">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <div className="w-9 h-9 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center shrink-0">{r.icon}</div>
                  <h2 className="font-display font-bold text-lg leading-tight flex-1 min-w-0">{r.title}</h2>
                  <span className={`chip text-[11px] border ${TAG_STYLE[r.tag] || 'bg-zinc-100 text-zinc-600'}`}>{r.tag}</span>
                </div>
                <div className="text-xs text-zinc-400 mb-3">{new Date(r.date + 'T00:00:00').toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                <ul className="space-y-2">
                  {r.items.map((it, j) => (
                    <li key={j} className="flex gap-2 text-sm text-zinc-700 leading-relaxed">
                      <span className="text-orange-500 mt-1 shrink-0">•</span>{it}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-zinc-400 mt-10">Have an idea? Send it from the <Link to="/" className="underline">home page</Link>. Data © Waka Kotahi NZTA & MetService.</p>
      </div>
    </div>
  );
}
